/**
 * Results Sidebar Component
 *
 * Provides view mode toggle (Comparisons | Responses) using DS Pill buttons.
 * Includes model filter checkboxes for filtering comparisons and responses.
 *
 * Step 1-2 of ResultsDemo_BabyStep_Plan implementation
 */

import React, { useState } from 'react';
import { useViewMode, type ViewMode } from '../state/viewMode';
// REMOVED: import { useModelFilters } from '../state/modelFilters';
// REMOVED: import { getModelLogo } from '../../../utils/logoMapping';
import { useDensity, type DensityOption } from '../state/density';
import type { ComparisonDetailView } from '../state/comparisonDetailView';
import type { ConsensusTab } from '../../../features/consensus';
import { isFeatureEnabled as isFeatureEnabledLegacy } from '../../../lib/flags';
import { isFeatureEnabled } from '../../../utils/featureFlags';
import { logger } from '../../../lib/logging';
import { ViewModeSlider } from '../ViewModeSlider';
import { useDevice } from '../../../features/consensus/hooks/useDevice';
import { DocumentTextIcon, TrophyIcon, ScaleIcon, FunnelIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { CompareDeck } from './CompareDeck';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';
import type { Response } from '../../../lib/types/results';
import { ModelFilter } from '../comparisons/ModelFilter';
import { SearchPopup } from './SearchPopup';
import { CollapsedListToolbar } from './CollapsedListToolbar';
import { ModelFilterModal } from '../comparisons/ModelFilterModal';
import { SidebarUsageTracker } from './SidebarUsageTracker';
import { SidebarTokenUsage } from './SidebarTokenUsage';
import { entitlementService } from '../../../services/entitlement';

export interface ResultsSidebarProps {
  className?: string;
  density?: DensityOption;
  onDensityChange?: (density: DensityOption) => void;
  cmpQuery?: string;
  onCmpQueryChange?: (query: string) => void;
  respQuery?: string;
  onRespQueryChange?: (query: string) => void;
  // Comparison detail view props
  isComparisonDetailOpen?: boolean;
  comparisonDetailView?: ComparisonDetailView;
  onComparisonDetailViewChange?: (view: ComparisonDetailView) => void;
  // Consensus tab props (for Compare Modes)
  consensusTab?: ConsensusTab;
  onConsensusTabChange?: (tab: ConsensusTab) => void;
  // Sidebar collapse props
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  // Sidebar visibility props
  hideCompletely?: boolean;
  // Task 2-5: SxS Compare Deck props
  compareDeckResponses?: Response[];
  onCompareDeckRemove?: (responseId: string) => void;
  onOpenSxS?: () => void;
  isSxSMode?: boolean;
  onExitSxS?: () => void;
  correlationId?: string;
  // Model Filter props
  modelFilterResponses?: Response[];
  modelFilterModelDetails?: Array<{ id: string; displayName: string; providerName: string }>;
  modelFilterSelectedModels?: Set<string>;
  modelFilterMode?: 'any' | 'all';
  onModelFilterChange?: (selectedModels: Set<string>, filterMode: 'any' | 'all') => void;
  // Empty state control
  hasComparisons?: boolean;
  // Feedback panel props
  feedbackComparisonId?: string | null;
  feedbackModelSettings?: Record<string, unknown>;
  // Anonymous user props
  isAnonymous?: boolean;
}

export const ResultsSidebar: React.FC<ResultsSidebarProps> = ({
  className = '',
  density: propDensity,
  onDensityChange,
  cmpQuery = '',
  onCmpQueryChange,
  respQuery = '',
  onRespQueryChange,
  isComparisonDetailOpen = false,
  comparisonDetailView = 'outputs',
  onComparisonDetailViewChange,
  consensusTab = 'heatmap',
  onConsensusTabChange,
  collapsed = false,
  onToggleCollapse,
  hideCompletely = false,
  compareDeckResponses = [],
  onCompareDeckRemove,
  onOpenSxS,
  isSxSMode = false,
  onExitSxS,
  correlationId,
  modelFilterResponses = [],
  modelFilterModelDetails = [],
  modelFilterSelectedModels = new Set(),
  modelFilterMode = 'any',
  onModelFilterChange,
  hasComparisons = true,
  feedbackComparisonId = null,
  feedbackModelSettings,
  isAnonymous = false
}) => {
  // Early return if sidebar should be completely hidden
  if (hideCompletely) {
    return null;
  }
  const { viewMode, setViewMode, isComparisonsView, isResponsesView } = useViewMode();
  // REMOVED: const { modelFilters, toggleModel, enableAllModels, allModelsEnabled } = useModelFilters();
  const { density: localDensity, setDensity: setLocalDensity } = useDensity();
  const density = propDensity ?? localDensity;
  const setDensity = onDensityChange ?? setLocalDensity;
  const { isMobile } = useDevice();
  const isSxSEnabled = true; // Always enabled - SxS comparison and scoring
  const [isSearchPopupOpen, setIsSearchPopupOpen] = useState(false);

  // Model filter modal state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Entitlement status for saved comparisons alert
  const [entitlementStatus, setEntitlementStatus] = React.useState<any>(null);
  const paywallEnabled = useFeatureFlag('PAYWALL');
  const tokenUsageEnabled = useFeatureFlag('TOKENUSAGE');

  // Fetch entitlements on mount
  React.useEffect(() => {
    if (!paywallEnabled) {
      console.log('[ResultsSidebar] Skipping entitlements fetch - PAYWALL disabled');
      return;
    }

    const fetchEntitlements = async () => {
      try {
        console.log('[ResultsSidebar] Fetching entitlement status...');
        const status = await entitlementService.getStatus();
        console.log('[ResultsSidebar] Entitlement status received:', {
          plan: status.plan?.code,
          hasComparisonsQuota: !!status.quotas?.comparisons,
          hasSavedComparisonsQuota: !!status.quotas?.savedComparisons,
          hasTotalComparisonsCount: status.totalComparisonsCount !== undefined,
          totalComparisonsCount: status.totalComparisonsCount,
          savedComparisonsQuota: status.quotas?.savedComparisons,
        });
        setEntitlementStatus(status);
      } catch (err) {
        console.error('[ResultsSidebar] Failed to fetch entitlements:', err);
      }
    };
    fetchEntitlements();
  }, [paywallEnabled]);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
  };

  const handleSearchPopupOpen = () => {
    setIsSearchPopupOpen(true);
  };

  const handleSearchPopupClose = () => {
    setIsSearchPopupOpen(false);
  };

  const handleSearchChange = (query: string) => {
    if (onCmpQueryChange) {
      onCmpQueryChange(query);
    }
  };

  return (
    <aside className={`bg-slate-800/50 overflow-y-auto md:overflow-hidden relative ${className}`}>
      {/* Toggle button - Desktop only, positioned on outer right edge */}
      {onToggleCollapse && !isMobile && (
        <button
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className="absolute -right-1 top-1/2 -translate-y-1/2 z-20 w-5 h-20 flex items-center justify-center text-slate-300 hover:text-cyan-300 hover:bg-slate-700/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 cursor-col-resize rounded-sm group"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg
            className="w-5 h-5 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            {collapsed ? (
              // Pointing right when collapsed (expand)
              <polyline points="9 18 15 12 9 6" />
            ) : (
              // Pointing left when expanded (collapse)
              <polyline points="15 18 9 12 15 6" />
            )}
          </svg>
        </button>
      )}

      {/* Collapsed sidebar toolbar - List view with collapsed sidebar */}
      {collapsed && !isComparisonDetailOpen && !isMobile && isComparisonsView && (
        <>
          <CollapsedListToolbar
            isComparisonsView={isComparisonsView}
            density={density}
            onDensityChange={setDensity}
            onSearchClick={handleSearchPopupOpen}
            cmpQuery={cmpQuery}
            onCmpQueryChange={onCmpQueryChange}
            modelFilterResponses={modelFilterResponses}
            modelFilterModelDetails={modelFilterModelDetails}
            modelFilterSelectedModels={modelFilterSelectedModels}
            modelFilterMode={modelFilterMode}
            onModelFilterChange={onModelFilterChange}
            onFilterClick={() => setIsFilterModalOpen(true)}
          />
        </>
      )}
      {!collapsed && (
        <div className="px-3 pb-3 pt-0 sm:px-4 sm:pb-4 sm:pt-4 space-y-4 sm:space-y-6">

        {/* Model Filters - REMOVED: Disabled to show all comparisons */}
        {/* <div className="pt-3 sm:pt-4 border-t border-slate-700/50">
          ... model filters UI removed ...
        </div> */}

        {/* Search input - only show for Comparisons view */}
        {!isComparisonDetailOpen && hasComparisons && (
          <>
            {/* Search input - only show for Comparisons view */}
            {isComparisonsView && onCmpQueryChange && (
              <div className="pt-3 sm:pt-4">
                <div className="flex items-center gap-2" data-testid="cmp-search">
                  <input
                    type="text"
                    aria-label="Search comparisons"
                    placeholder="Search comparisons..."
                    value={cmpQuery}
                    onChange={(e) => onCmpQueryChange(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  />
                  {cmpQuery && (
                    <button
                      aria-label="Clear search"
                      onClick={() => onCmpQueryChange('')}
                      className="p-2 rounded-lg bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Search input - only show for Responses view */}
            {isResponsesView && onRespQueryChange && (
              <div className="pt-3 sm:pt-4">
                <div className="flex items-center gap-2" data-testid="resp-search">
                  <input
                    type="text"
                    aria-label="Search responses"
                    placeholder="Search responses..."
                    value={respQuery}
                    onChange={(e) => onRespQueryChange(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  />
                  {respQuery && (
                    <button
                      aria-label="Clear search"
                      onClick={() => onRespQueryChange('')}
                      className="p-2 rounded-lg bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Model Filter - show for Comparisons list view only, desktop only */}
            {!isMobile && isComparisonsView && onModelFilterChange && (
              <div className="pt-3 sm:pt-4 border-t border-slate-700/50">
                <ModelFilter
                  responses={modelFilterResponses}
                  modelDetails={modelFilterModelDetails}
                  selectedModels={modelFilterSelectedModels}
                  filterMode={modelFilterMode}
                  onFilterChange={onModelFilterChange}
                />
              </div>
            )}

            {/* Layout slider - show for Comparisons list view only (not Responses) */}
            {!isMobile && isComparisonsView && !isSxSMode && (
              <div className="pt-6 sm:pt-8 border-t border-slate-700/50 space-y-4 sm:space-y-5">
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">
                    View Mode
                  </h3>
                  <ViewModeSlider value={density} onChange={setDensity} />
                </div>

                {paywallEnabled && (
                  <div className="mt-1">
                    <SidebarUsageTracker />
                  </div>
                )}
              </div>
            )}
          </>
        )}

      </div>
      )}

      {/* View tabs - OUTSIDE collapsed wrapper so they always render when detail is open */}
      {isComparisonDetailOpen && onComparisonDetailViewChange && !isResponsesView && (
        <>
          {/* Section 1: Comparison Detail View Selector (Responses/Scores/Consensus) - Desktop only, mobile tabs now in main header */}
          {!isMobile && (
            <div className={
              collapsed
                ? 'pt-3 flex flex-col items-center'
                : 'pt-0 sm:pt-0 px-3 sm:px-4 pb-3 sm:pb-4'
            }>
              {!collapsed && (
                <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">
                  Result Type
                </h3>
              )}
              <div className={
                collapsed
                  ? 'flex flex-col items-center gap-2'
                  : 'space-y-2'
              }>
                {/* Responses Button */}
                <button
                  onClick={() => onComparisonDetailViewChange('outputs')}
                  aria-label="Responses result type"
                  title={collapsed ? "Responses result type" : undefined}
                  className={
                    collapsed
                      ? `w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
                          comparisonDetailView === 'outputs'
                            ? 'bg-cyan-500/30 border-2 border-cyan-400/70 text-cyan-200 shadow-lg shadow-cyan-500/20'
                            : 'bg-slate-700/40 border border-slate-600/60 text-slate-300 hover:bg-slate-700/60 hover:text-cyan-300 hover:border-cyan-500/30'
                        }`
                      : `w-full flex items-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                          comparisonDetailView === 'outputs'
                            ? 'bg-cyan-500/30 border-2 border-cyan-400/70 text-cyan-200 shadow-lg shadow-cyan-500/20'
                            : 'bg-slate-700/40 border border-slate-600/60 text-slate-300 hover:bg-slate-700/60 hover:text-cyan-300 hover:border-cyan-500/30'
                        }`
                  }
                >
                  {collapsed ? (
                    <DocumentTextIcon className="w-6 h-6" />
                  ) : (
                    <span className="flex items-center gap-2">
                      <DocumentTextIcon className="w-5 h-5" />
                      <span>Responses</span>
                    </span>
                  )}
                </button>

                {/* Scores Button */}
                <button
                  onClick={() => !isAnonymous && onComparisonDetailViewChange('scoring')}
                  aria-label={isAnonymous ? "Scores (Sign up required)" : "Scores result type"}
                  title={collapsed ? (isAnonymous ? "Sign up to view scores" : "Scores result type") : undefined}
                  disabled={isAnonymous}
                  className={
                    collapsed
                      ? `w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
                          isAnonymous
                            ? 'bg-slate-800/30 border border-slate-700/50 text-slate-500 cursor-not-allowed opacity-60'
                            : comparisonDetailView === 'scoring'
                            ? 'bg-cyan-500/30 border-2 border-cyan-400/70 text-cyan-200 shadow-lg shadow-cyan-500/20'
                            : 'bg-slate-700/40 border border-slate-600/60 text-slate-300 hover:bg-slate-700/60 hover:text-cyan-300 hover:border-cyan-500/30'
                        }`
                      : `w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                          isAnonymous
                            ? 'bg-slate-800/30 border border-slate-700/50 text-slate-500 cursor-not-allowed opacity-60'
                            : comparisonDetailView === 'scoring'
                            ? 'bg-cyan-500/30 border-2 border-cyan-400/70 text-cyan-200 shadow-lg shadow-cyan-500/20'
                            : 'bg-slate-700/40 border border-slate-600/60 text-slate-300 hover:bg-slate-700/60 hover:text-cyan-300 hover:border-cyan-500/30'
                        }`
                  }
                >
                  {collapsed ? (
                    isAnonymous ? <LockClosedIcon className="w-5 h-5" /> : <TrophyIcon className="w-6 h-6" />
                  ) : (
                    <>
                      <span className="flex items-center gap-2">
                        <TrophyIcon className="w-5 h-5" />
                        <span>Scores</span>
                      </span>
                      {isAnonymous && <LockClosedIcon className="w-4 h-4 ml-2" />}
                    </>
                  )}
                </button>

                {/* Consensus button - gated by feature flag FF_RESULTS_CONSENSUS and requires 2+ responses */}
                {isFeatureEnabledLegacy('FF_RESULTS_CONSENSUS') && modelFilterResponses.length > 1 && (
                  <button
                    onClick={() => {
                      if (isAnonymous) return;
                      const startTime = performance.now();
                      const correlationId = crypto.randomUUID();
                      try {
                        onComparisonDetailViewChange('consensus');
                        logger.info('ui.tab.select', undefined, {
                          op: 'ui.tab.select',
                          component: 'ResultsSidebar',
                          view: 'consensus',
                          correlationId,
                          duration_ms: performance.now() - startTime,
                          success: true
                        });
                        logger.info('sidebar_compare_modes_hidden', undefined, {
                          op: 'sidebar_compare_modes_hidden',
                          component: 'ResultsSidebar',
                          view: 'consensus',
                          correlationId,
                          reason: 'top_tabs_own_controls'
                        });
                      } catch (error) {
                        logger.error('ui.tab.select', undefined, {
                          op: 'ui.tab.select',
                          component: 'ResultsSidebar',
                          view: 'consensus',
                          correlationId,
                          duration_ms: performance.now() - startTime,
                          success: false,
                          err_code: error instanceof Error ? error.name : 'UnknownError',
                          err_message: error instanceof Error ? error.message : String(error)
                        });
                        if (import.meta.env?.MODE !== 'test') {
                          throw error;
                        }
                      }
                    }}
                    aria-label={isAnonymous ? "Consensus (Sign up required)" : "Consensus result type"}
                    title={collapsed ? (isAnonymous ? "Sign up to view consensus" : "Consensus result type") : undefined}
                    disabled={isAnonymous}
                    className={
                      collapsed
                        ? `w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
                            isAnonymous
                              ? 'bg-slate-800/30 border border-slate-700/50 text-slate-500 cursor-not-allowed opacity-60'
                              : comparisonDetailView === 'consensus'
                              ? 'bg-cyan-500/30 border-2 border-cyan-400/70 text-cyan-200 shadow-lg shadow-cyan-500/20'
                              : 'bg-slate-700/40 border border-slate-600/60 text-slate-300 hover:bg-slate-700/60 hover:text-cyan-300 hover:border-cyan-500/30'
                          }`
                      : `w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                          isAnonymous
                            ? 'bg-slate-800/30 border border-slate-700/50 text-slate-500 cursor-not-allowed opacity-60'
                            : comparisonDetailView === 'consensus'
                            ? 'bg-cyan-500/30 border-2 border-cyan-400/70 text-cyan-200 shadow-lg shadow-cyan-500/20'
                            : 'bg-slate-700/40 border border-slate-600/60 text-slate-300 hover:bg-slate-700/60 hover:text-cyan-300 hover:border-cyan-500/30'
                        }`
                  }
                >
                  {collapsed ? (
                    isAnonymous ? <LockClosedIcon className="w-5 h-5" /> : <ScaleIcon className="w-6 h-6" />
                  ) : (
                    <>
                      <span className="flex items-center gap-2">
                        <ScaleIcon className="w-5 h-5" />
                        <span>Consensus</span>
                      </span>
                      {isAnonymous && <LockClosedIcon className="w-4 h-4 ml-2" />}
                    </>
                  )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section 2: Density Selector for Response Cards (Desktop Only, Responses Tab Only) */}
          {comparisonDetailView === 'outputs' && !isMobile && !isSxSMode && (
            collapsed ? (
              // Collapsed state: show view mode selector with eye icon
              <div className="pt-8 flex flex-col items-center">
                <ViewModeSlider
                  value={density}
                  onChange={setDensity}
                  collapsed={true}
                />
              </div>
            ) : (
              // Expanded state: show view mode selector with label
              <div className="pt-3 sm:pt-4 px-3 sm:px-4 pb-3 sm:pb-4 border-t border-slate-700/50">
                <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">
                  View Mode
                </h3>
                <ViewModeSlider
                  value={density}
                  onChange={setDensity}
                  collapsed={false}
                />
              </div>
            )
          )}

          {/* Usage Tracker - Desktop only, show when sidebar is expanded */}
          {paywallEnabled && !isMobile && !collapsed && (() => {
            const showTokenUsage = tokenUsageEnabled && (comparisonDetailView === 'scoring' || comparisonDetailView === 'consensus');
            const showTotalComparisons = comparisonDetailView === 'outputs';

            if (!showTokenUsage && !showTotalComparisons) {
              return null;
            }

            console.log('[ResultsSidebar] Usage Tracker render check:', {
              paywallEnabled,
              tokenUsageEnabled,
              isMobile,
              collapsed,
              comparisonDetailView,
              willShowTokenUsage: showTokenUsage,
              willShowUsageTracker: showTotalComparisons
            });
            return (
              <>
                {/* Token Usage - show on Scoring and Consensus tabs */}
                {showTokenUsage && (
                  <SidebarTokenUsage />
                )}
                {/* Show Total Comparisons on Responses tab */}
                {showTotalComparisons && (
                  <SidebarUsageTracker />
                )}
              </>
            );
          })()}

          {/* Task 2-5: SxS Compare Deck - CDV only, flag-gated, desktop-only */}
          {isSxSEnabled &&
            isComparisonDetailOpen &&
            !isResponsesView &&
            comparisonDetailView === 'outputs' &&
            modelFilterResponses.length > 1 && (
            <CompareDeck
              responses={compareDeckResponses}
              onRemove={onCompareDeckRemove}
              onOpenSxS={onOpenSxS}
              isSxSMode={isSxSMode}
              onExitSxS={onExitSxS}
              collapsed={collapsed}
              correlationId={correlationId}
            />
          )}
        </>
      )}

      {/* Search Popup Modal */}
      <SearchPopup
        isOpen={isSearchPopupOpen}
        searchQuery={cmpQuery}
        onSearchChange={handleSearchChange}
        onClose={handleSearchPopupClose}
        placeholder="Search comparisons..."
      />

      {/* Model Filter Modal - for collapsed sidebar */}
      {onModelFilterChange && (
        <ModelFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          responses={modelFilterResponses}
          modelDetails={modelFilterModelDetails}
          selectedModels={modelFilterSelectedModels}
          filterMode={modelFilterMode}
          onFilterChange={onModelFilterChange}
        />
      )}
    </aside>
  );
};
